import { router } from "@inertiajs/react"
import type { CanvasApiScope } from "@/lib/canvas-scope"
import { canvasApiBase, resolveCanvasApiScope } from "@/lib/canvas-scope"

const LEGACY_CANVAS_PARAM = "canvas_id"
const CANVAS_NUMBER_PARAM = "canvas"
const LEGACY_GENERATION_PARAM = "generation"
const GENERATION_NUMBER_PARAM = "gen"

export type CanvasUrlRef = { by: "number"; value: number } | { by: "id"; value: number }
export type CanvasBoardUrlRef = CanvasUrlRef | { by: "all" }

function positiveInt(raw: string | null): number | null {
  if (!raw || !/^\d+$/.test(raw)) return null
  const n = Number(raw)
  return n > 0 ? n : null
}

export function parseCanvasBoardRef(search: string): CanvasBoardUrlRef | null {
  const params = new URLSearchParams(search)
  if (params.get(CANVAS_NUMBER_PARAM) === "all") return { by: "all" }
  const byNumber = positiveInt(params.get(CANVAS_NUMBER_PARAM))
  if (byNumber != null) return { by: "number", value: byNumber }
  const byId = positiveInt(params.get(LEGACY_CANVAS_PARAM))
  if (byId != null) return { by: "id", value: byId }
  return null
}

export function parseCanvasGenerationRef(search: string): CanvasUrlRef | null {
  const params = new URLSearchParams(search)
  const byNumber = positiveInt(params.get(GENERATION_NUMBER_PARAM))
  if (byNumber != null) return { by: "number", value: byNumber }
  const byId = positiveInt(params.get(LEGACY_GENERATION_PARAM))
  if (byId != null) return { by: "id", value: byId }
  return null
}

/** Legacy `?generation=<pk>`. Prefer `parseCanvasGenerationRef`. */
export function parseCanvasGenerationId(search: string): number | null {
  const ref = parseCanvasGenerationRef(search)
  return ref?.by === "id" ? ref.value : null
}

export function canvasBoardHref(scopeOrProjectId: CanvasApiScope | number, canvasNumber: number): string {
  const scope = resolveCanvasApiScope(scopeOrProjectId)
  return `${canvasApiBase(scope)}?${CANVAS_NUMBER_PARAM}=${canvasNumber}`
}

export function canvasBoardAllHref(scopeOrProjectId: CanvasApiScope | number): string {
  const scope = resolveCanvasApiScope(scopeOrProjectId)
  return `${canvasApiBase(scope)}?${CANVAS_NUMBER_PARAM}=all`
}

export function canvasGenerationHref(
  scopeOrProjectId: CanvasApiScope | number,
  canvas: { number: number } | { id: number },
  generation: { number: number } | { id: number },
): string {
  const scope = resolveCanvasApiScope(scopeOrProjectId)
  const params = new URLSearchParams()
  if ("number" in canvas) params.set(CANVAS_NUMBER_PARAM, String(canvas.number))
  else params.set(LEGACY_CANVAS_PARAM, String(canvas.id))
  if ("number" in generation) params.set(GENERATION_NUMBER_PARAM, String(generation.number))
  else params.set(LEGACY_GENERATION_PARAM, String(generation.id))
  return `${canvasApiBase(scope)}?${params.toString()}`
}

export function visitCanvasGeneration(
  scopeOrProjectId: CanvasApiScope | number,
  canvas: { number: number } | { id: number },
  generation: { number: number } | { id: number },
): void {
  router.visit(canvasGenerationHref(scopeOrProjectId, canvas, generation))
}

export function syncCanvasGenerationUrl(args: { canvasNumber: number | "all"; generationNumber: number | null }): void {
  if (typeof window === "undefined") return
  const url = new URL(window.location.href)
  url.searchParams.set(CANVAS_NUMBER_PARAM, String(args.canvasNumber))
  url.searchParams.delete(LEGACY_CANVAS_PARAM)
  if (args.generationNumber != null) {
    url.searchParams.set(GENERATION_NUMBER_PARAM, String(args.generationNumber))
  } else {
    url.searchParams.delete(GENERATION_NUMBER_PARAM)
  }
  url.searchParams.delete(LEGACY_GENERATION_PARAM)
  const query = url.searchParams.toString()
  const next = query ? `${url.pathname}?${query}` : url.pathname
  window.history.replaceState(window.history.state, "", next)
}
